import { useMemo } from 'react';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import useTechnicianAssignments from '@/hooks/useTechnicianAssignments';
import { useRoutes } from './useTechnicianRoutes';
import type {
  TechnicianRouteWithDetails,
  RouteData,
  RouteStop,
  RouteStatus
} from '@/types/technicianRoutes';
import { formatRouteDate } from '@/lib/supabase/technicianRoutes';
import { dealerInfoId } from '../utils/routeBoardMapping';

interface Vehicle {
  vin: string;
  year: string;
  make: string;
  model: string;
  recalls: Array<{
    vin: string;
    campaign_number: string;
    recall_title: string;
    stock_number?: string;
    skill_level?: number;
    estimated_duration?: number;
    stop_type?: 'recall' | 'manual';
    manual_stop_reason?: string;
    notes?: string;
    dispatcher_note?: string;
    stop_kind?: 'business' | 'customer';
    contact_id?: string;
  }>;
}

interface DealerInfo {
  id: string;
  name: string;
  /** Customer stop (name is 'Customer stop'); contact fetched at print/display time */
  stopKind?: 'business' | 'customer';
  contactId?: string;
  addressLine2?: string;
  address: string;
  city: string;
  state: string;
  postalCode?: string;
  phone?: string;
  stats: {
    vehicles: number;
    recalls: number;
    estimatedTime: number;
  };
  vehicles: Vehicle[];
}

interface RoutePreviewStats {
  totalDealers: number;
  totalRecalls: number;
  totalVehicles: number;
  estimatedDuration: number;
}

// Extended stats when using stored routes
interface StoredRouteStats extends RoutePreviewStats {
  totalRoutes: number;
  totalDistanceMiles: number;
}

// Route with technician info for display
export interface TechnicianRoute {
  id: string;
  technicianId: string;
  technicianName: string;
  routeDate: string;
  status: RouteStatus;
  dealers: DealerInfo[];
  stats: {
    totalDealers: number;
    totalRecalls: number;
    totalVehicles: number;
    estimatedDuration: number;
    distanceMiles: number;
  };
  confirmedAt: string | null;
  // Route sequence for multiple routes per technician per day
  routeSequence: number;
}

interface UseRoutePreviewReturn {
  dealers: DealerInfo[];
  loading: boolean;
  error: string | null;
  stats: RoutePreviewStats;
  isAuthenticated: boolean;
}

// Extended return type when using stored routes
interface UseStoredRoutePreviewReturn {
  routes: TechnicianRoute[];
  dealers: DealerInfo[]; // Flattened dealers from all routes
  loading: boolean;
  error: string | null;
  stats: StoredRouteStats;
  isAuthenticated: boolean;
}

interface UseRoutePreviewOptions {
  // Use stored routes from technician_routes table
  useStoredRoutes?: boolean;
  // Date for stored routes (defaults to today)
  routeDate?: Date | string;
  // Status filter for stored routes
  status?: RouteStatus;
}

/**
 * Transform stored route data into DealerInfo format for display
 */
function transformRouteStopToDealerInfo(stop: RouteStop): DealerInfo {
  const vehicles: Vehicle[] = [];
  const vehicleMap = new Map<string, Vehicle>();

  // Group recalls by VIN to create vehicles
  stop.recalls.forEach(recall => {
    let vehicle = vehicleMap.get(recall.vin);
    if (!vehicle) {
      vehicle = {
        vin: recall.vin,
        year: String(recall.vehicle_year || 'Unknown'),
        make: recall.vehicle_make || 'Unknown',
        model: recall.vehicle_model || 'Unknown',
        recalls: []
      };
      vehicleMap.set(recall.vin, vehicle);
      vehicles.push(vehicle);
    }

    vehicle.recalls.push({
      vin: recall.vin,
      campaign_number: recall.campaign_number,
      recall_title: recall.recall_title,
      ...(recall.stock_number && { stock_number: recall.stock_number }),
      estimated_duration: recall.estimated_duration_minutes || 60,
      ...(recall.stop_type && { stop_type: recall.stop_type }),
      ...(recall.manual_stop_reason && { manual_stop_reason: recall.manual_stop_reason }),
      ...(recall.notes && { notes: recall.notes }),
      ...(recall.dispatcher_note && { dispatcher_note: recall.dispatcher_note }),
      ...(recall.stop_kind && { stop_kind: recall.stop_kind }),
      ...(recall.contact_id && { contact_id: recall.contact_id })
    });
  });

  return {
    id: dealerInfoId(stop),
    name: stop.dealer_name,
    ...(stop.stop_kind && { stopKind: stop.stop_kind }),
    ...(stop.contact_id && { contactId: stop.contact_id }),
    ...(stop.address_line2 && { addressLine2: stop.address_line2 }),
    address: stop.dealer_address || 'Address not available',
    city: stop.dealer_city || 'Unknown',
    state: stop.dealer_region || 'Unknown',
    postalCode: stop.dealer_postal_code || '',
    phone: stop.dealer_phone || '',
    stats: {
      vehicles: vehicles.length,
      recalls: stop.recall_count || stop.recalls.length,
      estimatedTime: stop.estimated_service_duration_minutes || stop.recalls.length * 60
    },
    vehicles
  };
}

/**
 * Transform a stored route into a TechnicianRoute for display
 */
function transformStoredRoute(route: TechnicianRouteWithDetails): TechnicianRoute {
  const routeData = route.route_data as RouteData;
  const stops = routeData?.stops || [];

  const dealers = stops.map(transformRouteStopToDealerInfo);

  return {
    id: route.id,
    technicianId: route.technician_id,
    technicianName: route.technician_name || 'Unknown',
    routeDate: route.route_date,
    status: route.status,
    dealers,
    stats: {
      totalDealers: route.total_stops || dealers.length,
      totalRecalls: route.total_recalls || dealers.reduce((sum, d) => sum + d.stats.recalls, 0),
      totalVehicles: dealers.reduce((sum, d) => sum + d.stats.vehicles, 0),
      estimatedDuration:
        route.total_duration_minutes || dealers.reduce((sum, d) => sum + d.stats.estimatedTime, 0),
      distanceMiles: route.total_distance_miles || 0
    },
    confirmedAt: route.confirmed_at,
    routeSequence: route.route_sequence || 1
  };
}

/**
 * Hook for route preview - supports both in-memory (selected recalls) and stored routes
 */
const useRoutePreview = (
  options?: UseRoutePreviewOptions
): UseRoutePreviewReturn | UseStoredRoutePreviewReturn => {
  const { useStoredRoutes = false, routeDate, status } = options || {};

  // In-memory mode: use selected recalls and assignments
  const {
    selectedRecalls,
    loading: recallsLoading,
    error: recallsError,
    isAuthenticated
  } = useSelectedRecalls();

  const {
    assignments,
    loading: assignmentsLoading,
    error: assignmentsError
  } = useTechnicianAssignments();

  // Stored routes mode: fetch from technician_routes table
  const formattedDate = routeDate ? formatRouteDate(routeDate) : formatRouteDate(new Date());
  const {
    data: storedRoutes,
    isLoading: routesLoading,
    error: routesError
  } = useRoutes({
    startDate: formattedDate,
    endDate: formattedDate,
    status,
    enabled: useStoredRoutes
  });

  // Transform stored routes into TechnicianRoute format
  // Filter out completed and cancelled routes - they shouldn't appear on the preview page
  const transformedRoutes = useMemo(() => {
    if (!useStoredRoutes || !storedRoutes) return [];

    const filtered = storedRoutes.filter(
      route => route.status !== 'completed' && route.status !== 'cancelled'
    );

    return filtered.map(transformStoredRoute);
  }, [useStoredRoutes, storedRoutes]);

  // Flatten all dealers from stored routes
  const storedDealers = useMemo(() => {
    return transformedRoutes.flatMap(route => route.dealers);
  }, [transformedRoutes]);

  // Stats for stored routes
  const storedStats: StoredRouteStats = useMemo(() => {
    return {
      totalRoutes: transformedRoutes.length,
      totalDealers: storedDealers.length,
      totalRecalls: transformedRoutes.reduce((sum, route) => sum + route.stats.totalRecalls, 0),
      totalVehicles: transformedRoutes.reduce((sum, route) => sum + route.stats.totalVehicles, 0),
      estimatedDuration: transformedRoutes.reduce(
        (sum, route) => sum + route.stats.estimatedDuration,
        0
      ),
      totalDistanceMiles: transformedRoutes.reduce(
        (sum, route) => sum + route.stats.distanceMiles,
        0
      )
    };
  }, [transformedRoutes, storedDealers]);

  // In-memory dealers (original logic)
  const inMemoryDealers = useMemo(() => {
    if (useStoredRoutes) return [];

    // Filter out recalls that have been finalized (confirmed, completed, or cancelled)
    // Once a recall's status is updated in route tracker, it should not appear in the route preview
    const unconfirmedRecalls = selectedRecalls.filter(recall => {
      const existingAssignment = assignments.find(
        a => a.vin === recall.vin && a.campaign_number === recall.campaign_number
      );

      if (!existingAssignment) {
        // No assignment exists, show the recall
        return true;
      }

      const assignmentStatus = (existingAssignment as { assignment_status?: string })
        .assignment_status;
      // Only show recalls that are still in active workflow states
      // Exclude: confirmed, completed, cancelled (these have been processed)
      // Include: assigned, in_progress (these can still be viewed)
      return (
        assignmentStatus !== 'confirmed' &&
        assignmentStatus !== 'completed' &&
        assignmentStatus !== 'cancelled'
      );
    });

    const dealerMap = new Map<string, DealerInfo>();

    unconfirmedRecalls.forEach(recall => {
      const dealerKey = recall.dealer_name;

      if (!dealerMap.has(dealerKey)) {
        dealerMap.set(dealerKey, {
          id: `dealer-${dealerKey.toLowerCase().replace(/\s+/g, '-')}`,
          name: dealerKey,
          address: recall.dealer_street || recall.dealer_address || 'Address not available',
          city: recall.dealer_city || 'Unknown',
          state: recall.dealer_region || 'Unknown',
          postalCode: recall.dealer_postal_code || '',
          phone: recall.dealer_phone || recall.seller_phone || '',
          stats: {
            vehicles: 0,
            recalls: 0,
            estimatedTime: 0
          },
          vehicles: []
        });
      }

      const dealer = dealerMap.get(dealerKey);
      if (dealer) {
        // Find or create vehicle entry
        let vehicle = dealer.vehicles.find(v => v.vin === recall.vin);
        if (!vehicle) {
          vehicle = {
            vin: recall.vin,
            year: String(recall.car_year || 'Unknown'),
            make: recall.make_name || 'Unknown',
            model: recall.model_name || 'Unknown',
            recalls: []
          };
          dealer.vehicles.push(vehicle);
          dealer.stats.vehicles++;
        }

        // Add recall to vehicle
        vehicle.recalls.push({
          vin: recall.vin,
          campaign_number: recall.campaign_number,
          recall_title: recall.recall_title,
          skill_level: recall.skill_level,
          ...(recall.stock_number && { stock_number: recall.stock_number }),
          estimated_duration: 60 // Default 60 minutes per recall
        });

        // Update dealer stats
        dealer.stats.recalls++;
        dealer.stats.estimatedTime += 60; // Add 60 minutes per recall
      }
    });

    return Array.from(dealerMap.values());
  }, [useStoredRoutes, selectedRecalls, assignments]);

  const inMemoryStats: RoutePreviewStats = useMemo(() => {
    return {
      totalDealers: inMemoryDealers.length,
      totalRecalls: inMemoryDealers.reduce((sum, dealer) => sum + dealer.stats.recalls, 0),
      totalVehicles: inMemoryDealers.reduce((sum, dealer) => sum + dealer.stats.vehicles, 0),
      estimatedDuration: inMemoryDealers.reduce(
        (sum, dealer) => sum + dealer.stats.estimatedTime,
        0
      )
    };
  }, [inMemoryDealers]);

  // Return based on mode
  if (useStoredRoutes) {
    return {
      routes: transformedRoutes,
      dealers: storedDealers,
      loading: routesLoading,
      error: routesError?.message || null,
      stats: storedStats,
      isAuthenticated: true // Stored routes require auth via RLS
    } as UseStoredRoutePreviewReturn;
  }

  const loading = recallsLoading || assignmentsLoading;
  const error = recallsError || assignmentsError || null;

  return {
    dealers: inMemoryDealers,
    loading,
    error,
    stats: inMemoryStats,
    isAuthenticated
  };
};

export default useRoutePreview;

// Type guard for stored routes mode
export function isStoredRoutePreview(
  result: UseRoutePreviewReturn | UseStoredRoutePreviewReturn
): result is UseStoredRoutePreviewReturn {
  return 'routes' in result;
}
